import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import {
  ScratchPadMode,
  ScratchPadPoint,
  ScratchPadStroke,
  ScratchPadTextItem,
  ScratchPadTool,
} from "./types";
import { contentCropBox, TEXT_BOX_HEIGHT } from "./thumbnailCrop";

/** Enough for the 40px chip on a 3x display, and no larger. */
const THUMBNAIL_PX = 120;
import { ScratchPadTextLayer } from "./ScratchPadTextLayer";
import { useDrawSurface } from "@/drawlock/useDrawSurface";
import { rectContainsPoint } from "@/drawlock/geometry";
import { onPressInterrupt } from "@/drawlock/interrupts";
import { markDrawRegion, unmarkDrawRegion } from "@/drawlock/shield";
import type { DrawSurfaceAdapter } from "@/drawlock/types";
import {
  SCRATCHPAD_PANEL_SELECTOR,
  SCRATCHPAD_SURFACE_ID,
} from "@/drawlock/surfaceIds";

type StrokeStyle = Readonly<{
  tool: ScratchPadTool;
  color: string;
  size: number;
}>;

type Props = Readonly<{
  disabled?: boolean;
  strokes: readonly ScratchPadStroke[];
  strokeStyle: StrokeStyle;
  /**
   * The pen the space-bar draw lock writes with, regardless of the tool the
   * toolbar shows. Space always means pen — the same contract the tldraw
   * surfaces implement by force-switching to the draw tool on engage.
   */
  penStrokeStyle: StrokeStyle;
  onCommitStroke: (stroke: ScratchPadStroke) => void;
  onStrokeStart?: () => void;
  /** A started stroke was thrown away before it committed. */
  onStrokeDiscarded?: () => void;
  /** The strokes one eraser press swept, in no particular order. */
  onEraseStrokes?: (strokes: readonly ScratchPadStroke[]) => void;
  onDrawingIdle?: () => void;
  drawingIdleMs?: number;
  mode: ScratchPadMode;
  textItems: readonly ScratchPadTextItem[];
  activeTextId: string | null;
  onChangeText: (id: string, text: string) => void;
  onMoveText: (id: string, x: number, y: number) => void;
  onActivateText: (id: string) => void;
  onRemoveText: (id: string) => void;
  virtualHeightMultiplier?: number;
}>;

export type ScratchPadCanvasHandle = Readonly<{
  exportPngDataUrl: () => Promise<string | null>;
  exportThumbnailDataUrl: () => Promise<string | null>;
}>;

/** Past this the stroke is real ink, so a landing touch is the hand, not a pan. */
const PAN_TAKEOVER_MAX_POINTS = 4;

const averageTouchY = (touches: TouchList) => {
  let sum = 0;
  for (let i = 0; i < touches.length; i += 1) sum += touches[i].clientY;
  return sum / Math.max(1, touches.length);
};

const toNormalizedPoint = (
  x: number,
  y: number,
  w: number,
  h: number,
  pressure?: number,
): ScratchPadPoint => [x / w, y / h, pressure];

const fromNormalizedPoint = (p: ScratchPadPoint, w: number, h: number) => ({
  x: p[0] * w,
  y: p[1] * h,
});

const applyStrokeStyle = (
  ctx: CanvasRenderingContext2D,
  s: ScratchPadStroke,
) => {
  ctx.lineWidth = s.size;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.globalCompositeOperation =
    s.tool === "eraser" ? "destination-out" : "source-over";
  ctx.strokeStyle = s.tool === "eraser" ? "rgba(0,0,0,1)" : s.color;
};

const drawStroke = (
  ctx: CanvasRenderingContext2D,
  stroke: ScratchPadStroke,
  w: number,
  h: number,
) => {
  const pts = stroke.points;
  if (pts.length === 0) return;
  applyStrokeStyle(ctx, stroke);
  const first = fromNormalizedPoint(pts[0], w, h);
  ctx.beginPath();
  ctx.moveTo(first.x, first.y);
  for (let i = 1; i < pts.length; i += 1) {
    const next = fromNormalizedPoint(pts[i], w, h);
    ctx.lineTo(next.x, next.y);
  }
  ctx.stroke();
};

const distSq = (ax: number, ay: number, bx: number, by: number) => {
  const dx = ax - bx;
  const dy = ay - by;
  return dx * dx + dy * dy;
};

const distToSegmentSq = (
  px: number,
  py: number,
  ax: number,
  ay: number,
  bx: number,
  by: number,
) => {
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const abLenSq = abx * abx + aby * aby;
  const t = abLenSq === 0 ? 0 : (apx * abx + apy * aby) / abLenSq;
  const clamped = Math.max(0, Math.min(1, t));
  const cx = ax + abx * clamped;
  const cy = ay + aby * clamped;
  return distSq(px, py, cx, cy);
};

/** Sign of the turn from C->D onto C->P; zero when the three are collinear. */
const turn = (
  cx: number,
  cy: number,
  dx: number,
  dy: number,
  px: number,
  py: number,
) => (dx - cx) * (py - cy) - (dy - cy) * (px - cx);

const segmentsCross = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
) => {
  const a = turn(cx, cy, dx, dy, ax, ay);
  const b = turn(cx, cy, dx, dy, bx, by);
  const c = turn(ax, ay, bx, by, cx, cy);
  const d = turn(ax, ay, bx, by, dx, dy);
  return a > 0 !== b > 0 && c > 0 !== d > 0;
};

/**
 * Distance between two segments. Segments that cross are zero apart; otherwise
 * the closest approach is at one of the four endpoints, so the four
 * point-to-segment distances cover it.
 */
const distSegmentToSegmentSq = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
  cx: number,
  cy: number,
  dx: number,
  dy: number,
) => {
  if (segmentsCross(ax, ay, bx, by, cx, cy, dx, dy)) return 0;
  return Math.min(
    distToSegmentSq(ax, ay, cx, cy, dx, dy),
    distToSegmentSq(bx, by, cx, cy, dx, dy),
    distToSegmentSq(cx, cy, ax, ay, bx, by),
    distToSegmentSq(dx, dy, ax, ay, bx, by),
  );
};

/**
 * Whether an eraser moving from (ax,ay) to (bx,by) passes within `tolerance`
 * of `stroke`. Each of the stroke's segments is measured against the whole
 * move once, so the cost is the stroke's length and not the move's — sampling
 * the move instead put one full scan per few pixels of travel into the single
 * event that reported a flick. A press that has not moved is the degenerate
 * case where the two endpoints coincide.
 */
const sweepHitsStroke = (
  stroke: ScratchPadStroke,
  ax: number,
  ay: number,
  bx: number,
  by: number,
  w: number,
  h: number,
  tolerance: number,
) => {
  const pts = stroke.points;
  if (pts.length === 0) return false;
  const tolSq = tolerance * tolerance;
  let prev = fromNormalizedPoint(pts[0], w, h);
  if (pts.length === 1) {
    return distToSegmentSq(prev.x, prev.y, ax, ay, bx, by) <= tolSq;
  }
  for (let i = 1; i < pts.length; i += 1) {
    const next = fromNormalizedPoint(pts[i], w, h);
    const gap = distSegmentToSegmentSq(
      ax,
      ay,
      bx,
      by,
      prev.x,
      prev.y,
      next.x,
      next.y,
    );
    if (gap <= tolSq) return true;
    prev = next;
  }
  return false;
};

const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) => {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.quadraticCurveTo(x + w, y + h, x + w - radius, y + h);
  ctx.lineTo(x + radius, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

const wrapLines = (
  ctx: CanvasRenderingContext2D,
  text: string,
  maxWidth: number,
) => {
  const lines: string[] = [];
  text.split("\n").forEach((block) => {
    const words = block.split(" ");
    let current = "";
    words.forEach((word, index) => {
      const next = current ? `${current} ${word}` : word;
      if (ctx.measureText(next).width <= maxWidth) {
        current = next;
        if (index !== words.length - 1) return;
      }
      if (current) lines.push(current);
      current = word;
      if (index === words.length - 1) lines.push(current);
    });
    if (words.length === 0) lines.push("");
  });
  return lines;
};

const clearCanvas = (
  canvas: HTMLCanvasElement,
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
) => {
  const dpr = globalThis.devicePixelRatio || 1;
  canvas.width = Math.max(1, Math.round(w * dpr));
  canvas.height = Math.max(1, Math.round(h * dpr));
  canvas.style.width = `${w}px`;
  canvas.style.height = `${h}px`;
  ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  ctx.clearRect(0, 0, w, h);
};

export const ScratchPadCanvas = forwardRef<ScratchPadCanvasHandle, Props>(
  (
    {
      disabled,
      strokes,
      strokeStyle,
      penStrokeStyle,
      onCommitStroke,
      onStrokeStart,
      onStrokeDiscarded,
      onEraseStrokes,
      onDrawingIdle,
      drawingIdleMs = 700,
      mode,
      textItems,
      activeTextId,
      onChangeText,
      onMoveText,
      onActivateText,
      onRemoveText,
      virtualHeightMultiplier = 1,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const scrollParentRef = useRef<HTMLElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);
    const idleTimerRef = useRef<number | null>(null);
    const thumbnailCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const [visibleHeight, setVisibleHeight] = useState(0);
    const activeRef = useRef<{
      points: ScratchPadPoint[];
      tool: ScratchPadTool;
      color: string;
      size: number;
      lastClientX: number;
      lastClientY: number;
      w: number;
      h: number;
      /** null for a space-bar stroke: the lock owns no pointer of its own. */
      pointerId: number | null;
      pointerType: string | null;
    } | null>(null);
    const detachGlobalReleaseRef = useRef<(() => void) | null>(null);
    /**
     * The eraser press in flight. Holds the swept strokes themselves rather
     * than their positions: a press outlives any number of renders, and an
     * undo or a clear during one would renumber `strokes` underneath it.
     * `lastX`/`lastY` are the previous sample, in canvas pixels.
     */
    const eraseRef = useRef<{
      pointerId: number;
      pointerType: string;
      swept: Set<ScratchPadStroke>;
      lastX: number;
      lastY: number;
      lastScrollTop: number;
    } | null>(null);
    /** The two-finger scroll in progress; non-null only while panning. */
    const panRef = useRef<{
      startAvgY: number;
      startScrollTop: number;
    } | null>(null);
    /**
     * True while the space bar is holding the pen down. The locked path and
     * the ordinary press-the-trackpad path are mutually exclusive: whichever
     * owns the stroke, the other one stands down.
     */
    const lockEngagedRef = useRef(false);

    const virtualHeight = visibleHeight * virtualHeightMultiplier;

    const currentStrokeTemplate = useMemo(
      () => ({
        tool: strokeStyle.tool,
        color: strokeStyle.color,
        size: strokeStyle.size,
      }),
      [strokeStyle.color, strokeStyle.size, strokeStyle.tool],
    );

    const redrawAll = () => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const { width, height } = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(width));
      const h = Math.max(1, Math.floor(height));
      clearCanvas(canvas, ctx, w, h);
      survivingStrokes().forEach((s) => drawStroke(ctx, s, w, h));
    };

    /**
     * `strokes` minus whatever the eraser has swept but not yet committed, so
     * ink disappears under the drag rather than on release.
     */
    const survivingStrokes = useCallback(() => {
      const swept = eraseRef.current?.swept;
      if (!swept?.size) return strokes;
      return strokes.filter((stroke) => !swept.has(stroke));
    }, [strokes]);

    /** Every stroke that should be painted, including one still under the pen. */
    const paintableStrokes = useCallback(() => {
      const list = survivingStrokes();
      const active = activeRef.current;
      // A stroke the draw lock is holding open is not in `strokes` yet, so an
      // export taken mid-stroke would ship the drawing without that ink.
      return active && active.points.length > 0 ? [...list, active] : list;
    }, [survivingStrokes]);

    /** Ruled lines, but only the ones inside the region being painted. */
    const paintRules = (
      ctx: CanvasRenderingContext2D,
      fromY: number,
      toY: number,
      w: number,
      lineWidth: number,
    ) => {
      ctx.strokeStyle = "rgba(15, 23, 42, 0.08)";
      ctx.lineWidth = lineWidth;
      for (let y = Math.floor(fromY / 36) * 36; y < toY; y += 36) {
        ctx.beginPath();
        ctx.moveTo(0, y + 0.5);
        ctx.lineTo(w, y + 0.5);
        ctx.stroke();
      }
    };

    /** Strokes and text boxes. Uploaded images travel separately and are
     * deliberately not baked in. */
    const paintInk = (
      ctx: CanvasRenderingContext2D,
      w: number,
      h: number,
      list: readonly ScratchPadStroke[],
    ) => {
      list.forEach((stroke) => drawStroke(ctx, stroke, w, h));
      textItems.forEach((item) => {
        const x = item.x * w;
        const y = item.y * h;
        const bw = item.w * w;
        const paddingX = 18;
        const paddingY = 14;
        ctx.lineWidth = 1;
        ctx.strokeStyle = "rgba(226, 232, 240, 1)";
        drawRoundedRect(ctx, x, y, bw, TEXT_BOX_HEIGHT, 18);
        ctx.stroke();
        const textValue = item.text.trim() ? item.text : "Type here...";
        ctx.fillStyle = item.text.trim()
          ? "rgba(71, 85, 105, 1)"
          : "rgba(148, 163, 184, 1)";
        ctx.font = "500 18px Shantell Sans, sans-serif";
        ctx.textBaseline = "top";
        const lines = wrapLines(ctx, textValue, bw - paddingX * 2);
        lines.forEach((line, index) => {
          ctx.fillText(line, x + paddingX, y + paddingY + index * 26);
        });
      });
    };

    const pageSize = () => {
      const container = containerRef.current;
      if (!container) return null;
      const { width, height } = container.getBoundingClientRect();
      return {
        w: Math.max(1, Math.floor(width)),
        h: Math.max(1, Math.floor(height)),
      };
    };

    /** The full page, as submitted to the tutor. */
    const exportPngDataUrl = useCallback(async () => {
      const size = pageSize();
      if (!size) return null;
      const { w, h } = size;
      const offscreen = document.createElement("canvas");
      offscreen.width = w;
      offscreen.height = h;
      const ctx = offscreen.getContext("2d");
      if (!ctx) return null;
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, w, h);
      paintRules(ctx, 0, h, w, 1);
      paintInk(ctx, w, h, paintableStrokes());
      return offscreen.toDataURL("image/png");
    }, [paintableStrokes, textItems]);

    /**
     * A small square of just the drawing, for the chat bar's chip.
     *
     * Painted directly at thumbnail scale into one reused canvas rather than
     * rendered full-page and cropped: the page is mostly blank, so cropping
     * afterwards throws away the large majority of the pixels it just painted,
     * once per debounce while the student is still drawing.
     */
    const exportThumbnailDataUrl = useCallback(async () => {
      const size = pageSize();
      if (!size) return null;
      const { w, h } = size;

      const list = paintableStrokes();
      const box = contentCropBox(list, textItems, w, h);
      if (!box) return null;

      let canvas = thumbnailCanvasRef.current;
      if (!canvas) {
        canvas = document.createElement("canvas");
        canvas.width = THUMBNAIL_PX;
        canvas.height = THUMBNAIL_PX;
        thumbnailCanvasRef.current = canvas;
      }
      const ctx = canvas.getContext("2d");
      if (!ctx) return null;

      const scale = THUMBNAIL_PX / box.size;
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, THUMBNAIL_PX, THUMBNAIL_PX);
      // Anything outside the box is clipped by the canvas bounds for free.
      ctx.setTransform(scale, 0, 0, scale, -box.x * scale, -box.y * scale);
      paintRules(ctx, box.y, box.y + box.size, w, 1 / scale);
      paintInk(ctx, w, h, list);
      // Opaque white ground, so there is no alpha worth paying PNG for.
      return canvas.toDataURL("image/jpeg", 0.6);
    }, [paintableStrokes, textItems]);

    const clearIdleTimer = () => {
      if (idleTimerRef.current == null) return;
      globalThis.clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    };

    const scheduleIdleCallback = () => {
      if (!onDrawingIdle) return;
      clearIdleTimer();
      idleTimerRef.current = globalThis.setTimeout(
        onDrawingIdle,
        drawingIdleMs,
      );
    };

    // Track the scroll parent's visible height
    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const scrollParent = container.parentElement;
      if (!scrollParent) return;
      scrollParentRef.current = scrollParent;

      const updateHeight = () => {
        const { height } = scrollParent.getBoundingClientRect();
        setVisibleHeight(Math.max(1, Math.floor(height)));
      };

      updateHeight();
      const ro = new ResizeObserver(updateHeight);
      ro.observe(scrollParent);
      return () => ro.disconnect();
    }, []);

    useEffect(() => {
      const container = containerRef.current;
      if (!container) return;
      const ro = new ResizeObserver(() => redrawAll());
      ro.observe(container);
      return () => ro.disconnect();
    }, [strokes]);

    useEffect(() => {
      redrawAll();
    }, [strokes, virtualHeight]);

    useEffect(
      () => () => {
        clearIdleTimer();
        detachGlobalRelease();
        thumbnailCanvasRef.current = null;
      },
      [],
    );

    useEffect(() => {
      if (!activeRef.current && !eraseRef.current) return;
      if (disabled || mode === "text") endPress();
    }, [disabled, mode]);

    useImperativeHandle(
      ref,
      () => ({
        exportPngDataUrl,
        exportThumbnailDataUrl,
      }),
      [exportPngDataUrl, exportThumbnailDataUrl],
    );

    /**
     * Starts a stroke at a viewport point; returns false if none could start.
     * Only the draw lock calls this — `template` lets it write with the pen
     * even while the toolbar is on the eraser. An eraser template is refused:
     * a synthesized stroke must never silently erase under the student's hand.
     */
    const beginStrokeAt = (
      clientX: number,
      clientY: number,
      template: StrokeStyle = currentStrokeTemplate,
    ): boolean => {
      if (disabled) return false;
      if (mode === "text") return false;
      if (!canvasRef.current) return false;
      const container = containerRef.current;
      if (!container) return false;
      if (template.tool === "eraser") return false;
      clearIdleTimer();
      const rect = container.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      activeRef.current = {
        // A hovering pointer reports pressure 0; the renderer sizes strokes
        // from stroke.size, so a nominal value keeps the data sane.
        points: [
          toNormalizedPoint(clientX - rect.left, clientY - rect.top, w, h, 0.5),
        ],
        ...template,
        lastClientX: clientX,
        lastClientY: clientY,
        w,
        h,
        pointerId: null,
        pointerType: null,
      };
      return true;
    };

    /** A space-bar stroke accepts every move; a pressed one only its own. */
    const ownsPress = (pointerId: number) => {
      const erase = eraseRef.current;
      if (erase) return erase.pointerId === pointerId;
      const active = activeRef.current;
      if (!active) return false;
      return active.pointerId == null || active.pointerId === pointerId;
    };

    /**
     * Sweeps everything under the eraser between the last sample and this one.
     * `pointermove` can jump much further than the disc is wide, so testing
     * only where the pointer was sampled steps over whatever lay in between.
     */
    const eraseTo = (x: number, y: number, w: number, h: number) => {
      const erase = eraseRef.current;
      if (!erase) return;
      const tolerance = strokeStyle.size / 2 + 6;
      // A press survives the paper scrolling under it, and these coordinates
      // are relative to the page, not the screen — so the same nib position
      // reads somewhere new and the move between samples spans the scroll.
      // Re-anchor on it rather than sweeping the ink the paper slid past.
      const scrollTop = scrollParentRef.current?.scrollTop ?? 0;
      const anchored = scrollTop === erase.lastScrollTop;
      const ax = anchored ? erase.lastX : x;
      const ay = anchored ? erase.lastY : y;
      let swept = false;
      for (let i = strokes.length - 1; i >= 0; i -= 1) {
        const stroke = strokes[i];
        if (erase.swept.has(stroke)) continue;
        if (!sweepHitsStroke(stroke, ax, ay, x, y, w, h, tolerance)) continue;
        erase.swept.add(stroke);
        swept = true;
      }
      erase.lastX = x;
      erase.lastY = y;
      erase.lastScrollTop = scrollTop;
      if (swept) redrawAll();
    };

    /** Hands the whole sweep over in one go; a press erases once or not at all. */
    const flushErase = () => {
      const erase = eraseRef.current;
      eraseRef.current = null;
      if (!erase || erase.swept.size === 0) return;
      onEraseStrokes?.([...erase.swept]);
    };

    /** Ends whichever kind of press is in flight. Both detach the release. */
    const endPress = () => {
      flushErase();
      commitIfActive();
    };

    /**
     * Throws away the touch stroke in flight, ink included. Past two points it
     * must also retract the chip: onStrokeStart raised one, and a stroke that
     * never commits moves no pending-work flag to lower it again.
     */
    const discardTouchStroke = () => {
      const erase = eraseRef.current;
      if (erase && erase.pointerType !== "pen") {
        eraseRef.current = null;
        detachGlobalRelease();
        redrawAll();
        return;
      }
      const active = activeRef.current;
      if (!active || active.pointerType === "pen") return;
      activeRef.current = null;
      detachGlobalRelease();
      redrawAll();
      if (active.points.length >= 2) onStrokeDiscarded?.();
    };

    const handlePointerDown = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      if (mode === "text") return;
      // The space bar owns the stroke right now — ignore the trackpad.
      if (lockEngagedRef.current) return;
      // Fingers are scrolling the paper; a third one is not drawing on it.
      if (panRef.current) return;
      // An eraser press owns the surface until it lifts; a second pointer
      // during one is the resting hand, not a new erase.
      if (eraseRef.current) return;
      if (!canvasRef.current) return;
      const inFlight = activeRef.current;
      if (inFlight) {
        // A second pointer mid-stroke is the writing hand settling, except a
        // pen over touch — the pen is deliberate, so the touch was the palm.
        if (e.pointerType !== "pen" || inFlight.pointerType === "pen") return;
        discardTouchStroke();
      }
      clearIdleTimer();
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const w = Math.max(1, rect.width);
      const h = Math.max(1, rect.height);
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      if (currentStrokeTemplate.tool === "eraser") {
        eraseRef.current = {
          pointerId: e.pointerId,
          pointerType: e.pointerType,
          swept: new Set(),
          lastX: x,
          lastY: y,
          lastScrollTop: scrollParentRef.current?.scrollTop ?? 0,
        };
        eraseTo(x, y, w, h);
        e.currentTarget.setPointerCapture(e.pointerId);
        attachGlobalRelease(e.pointerId);
        return;
      }
      activeRef.current = {
        points: [toNormalizedPoint(x, y, w, h, e.pressure)],
        ...currentStrokeTemplate,
        lastClientX: e.clientX,
        lastClientY: e.clientY,
        w,
        h,
        pointerId: e.pointerId,
        pointerType: e.pointerType,
      };
      e.currentTarget.setPointerCapture(e.pointerId);
      attachGlobalRelease(e.pointerId);
    };

    const drawLiveSegment = (from: ScratchPadPoint, to: ScratchPadPoint) => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;
      const active = activeRef.current;
      if (!active) return;
      const w = active.w;
      const h = active.h;
      applyStrokeStyle(ctx, {
        tool: active.tool,
        color: active.color,
        size: active.size,
        points: [],
      });
      const a = fromNormalizedPoint(from, w, h);
      const b = fromNormalizedPoint(to, w, h);
      ctx.beginPath();
      ctx.moveTo(a.x, a.y);
      ctx.lineTo(b.x, b.y);
      ctx.stroke();
    };

    const handlePointerMove = (e: ReactPointerEvent<HTMLCanvasElement>) => {
      if (disabled) return;
      if (mode === "text") return;
      const erase = eraseRef.current;
      if (erase) {
        if (erase.pointerId !== e.pointerId) return;
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        eraseTo(
          e.clientX - rect.left,
          e.clientY - rect.top,
          Math.max(1, rect.width),
          Math.max(1, rect.height),
        );
        scheduleIdleCallback();
        return;
      }
      const active = activeRef.current;
      if (!active) return;
      if (!ownsPress(e.pointerId)) return;
      const container = containerRef.current;
      if (!container) return;
      if (
        Math.abs(e.clientX - active.lastClientX) +
          Math.abs(e.clientY - active.lastClientY) <
        1
      )
        return;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      const next = toNormalizedPoint(x, y, active.w, active.h, e.pressure);
      const prev = active.points[active.points.length - 1];
      active.points = [...active.points, next];
      // Two points is what commitIfActive keeps and what counts as content, so
      // this is the first moment the stroke is known to be a stroke.
      if (active.points.length === 2) onStrokeStart?.();
      active.lastClientX = e.clientX;
      active.lastClientY = e.clientY;
      drawLiveSegment(prev, next);
      scheduleIdleCallback();
    };

    const detachGlobalRelease = () => {
      const detach = detachGlobalReleaseRef.current;
      if (!detach) return;
      detachGlobalReleaseRef.current = null;
      detach();
    };

    const commitIfActive = () => {
      const active = activeRef.current;
      detachGlobalRelease();
      if (!active) return;
      activeRef.current = null;
      if (active.points.length < 2) return;
      onCommitStroke({
        tool: active.tool,
        color: active.color,
        size: active.size,
        points: active.points,
      });
    };

    // Any interruption that could swallow the pointerup (context menu on
    // right-click, focus loss, tab switch, escape, drag off the window) must
    // release the pressed state — otherwise pointer capture leaks and the pad
    // keeps drawing on hover.
    const attachGlobalRelease = (pointerId: number) => {
      detachGlobalRelease();
      const onRelease = () => {
        endPress();
        scheduleIdleCallback();
      };
      // The window hears every pointer's release; only this stroke's ends it.
      const onPointerRelease = (e: PointerEvent) => {
        if (e.pointerId !== pointerId) return;
        onRelease();
      };
      const onKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") onRelease();
      };
      // blur/pagehide/tab-hide come from the shared list; the rest are
      // stroke-specific and stay here.
      const unsubscribeInterrupts = onPressInterrupt(onRelease);
      globalThis.addEventListener("pointerup", onPointerRelease);
      globalThis.addEventListener("pointercancel", onPointerRelease);
      globalThis.addEventListener("contextmenu", onRelease);
      globalThis.addEventListener("keydown", onKey);
      detachGlobalReleaseRef.current = () => {
        unsubscribeInterrupts();
        globalThis.removeEventListener("pointerup", onPointerRelease);
        globalThis.removeEventListener("pointercancel", onPointerRelease);
        globalThis.removeEventListener("contextmenu", onRelease);
        globalThis.removeEventListener("keydown", onKey);
      };
    };

    const handlePointerUpWithIdle = (
      e: ReactPointerEvent<HTMLCanvasElement>,
    ) => {
      // A stray button release must not end a space-bar stroke.
      if (lockEngagedRef.current) return;
      if (!ownsPress(e.pointerId)) return;
      endPress();
      scheduleIdleCallback();
    };
    const handlePointerCancelWithIdle = (
      e: ReactPointerEvent<HTMLCanvasElement>,
    ) => {
      if (lockEngagedRef.current) return;
      if (!ownsPress(e.pointerId)) return;
      endPress();
      scheduleIdleCallback();
    };

    // --- space-bar draw lock -------------------------------------------------
    // Held via refs so the adapter identity never changes between renders.
    const drawLockAdapter = useMemo<DrawSurfaceAdapter>(
      () => ({
        id: SCRATCHPAD_SURFACE_ID,
        canEngage: () =>
          !lockEngagedRef.current && !activeRef.current && !eraseRef.current,
        containsPoint: (point) =>
          rectContainsPoint(
            containerRef.current?.getBoundingClientRect(),
            point,
          ),
        // The whole panel, not just the canvas — the toolbar is a sibling of
        // the canvas, and its buttons must not veto the space bar. No panel
        // means the marker was lost, which un-owns the toolbar; say no rather
        // than quietly answering for the canvas alone.
        ownsElement: (element) =>
          containerRef.current
            ?.closest(SCRATCHPAD_PANEL_SELECTOR)
            ?.contains(element) ?? false,
        engage: ({ x, y }) => {
          // Space always means pen: hand the lock the pen template so it can
          // write even while the toolbar is on the eraser. The toolbar tool is
          // untouched — the eraser is still selected after release, matching
          // the tldraw surfaces' stash-and-restore behaviour.
          const style = penStrokeStyleRef.current;
          if (!beginStrokeAtRef.current(x, y, style)) {
            return false;
          }
          // The draw cursor is the only cursor while the lock holds, so the
          // real one goes. The mark lands on the container, not on an overlay:
          // the moves that extend a stroke arrive on the canvas below, and an
          // element over it would take them instead.
          markContainerRef.current(true);
          lockEngagedRef.current = true;
          return true;
        },
        release: (_reason, cancel) => {
          if (!lockEngagedRef.current) return;
          lockEngagedRef.current = false;
          markContainerRef.current(false);
          if (cancel) {
            activeRef.current = null;
            return;
          }
          commitIfActiveRef.current();
          scheduleIdleCallbackRef.current();
        },
      }),
      [],
    );

    /** The pad is a draw-cursor region — real cursor hidden, dot shown in
     *  the pen colour — whenever it can take ink: while the lock holds, or
     *  while the toolbar is on the pen. The eraser and text keep the real
     *  cursor. The lock always writes with the pen, so the mark carries the
     *  pen colour even when the toolbar shows the eraser. */
    const isPenRegion = useCallback(
      (locked: boolean) =>
        !disabled && mode === "draw" && (locked || strokeStyle.tool === "pen"),
      [disabled, mode, strokeStyle.tool],
    );
    const markContainer = useCallback(
      (locked: boolean) => {
        const container = containerRef.current;
        if (!container) return;
        if (isPenRegion(locked))
          markDrawRegion(container, penStrokeStyle.color);
        else unmarkDrawRegion(container);
      },
      [isPenRegion, penStrokeStyle.color],
    );
    // Tool, colour, mode and disabled changes re-mark without any lock event.
    useEffect(() => {
      markContainer(lockEngagedRef.current);
    }, [markContainer]);

    // Latest closures for the stable adapter above.
    const markContainerRef = useRef(markContainer);
    markContainerRef.current = markContainer;
    const beginStrokeAtRef = useRef(beginStrokeAt);
    beginStrokeAtRef.current = beginStrokeAt;
    const penStrokeStyleRef = useRef(penStrokeStyle);
    penStrokeStyleRef.current = penStrokeStyle;
    const commitIfActiveRef = useRef(commitIfActive);
    commitIfActiveRef.current = commitIfActive;
    const scheduleIdleCallbackRef = useRef(scheduleIdleCallback);
    scheduleIdleCallbackRef.current = scheduleIdleCallback;
    const modeRef = useRef(mode);
    modeRef.current = mode;
    const disabledRef = useRef(disabled);
    disabledRef.current = disabled;
    const discardTouchStrokeRef = useRef(discardTouchStroke);
    discardTouchStrokeRef.current = discardTouchStroke;

    /**
     * Hand-driven touch scrolling, because Safari/iPadOS ignores
     * `touch-action: none` under a scrollable ancestor and fires
     * `pointercancel` once it claims the drag as a pan — dropping the stroke.
     * See "Scratchpad touch gestures" in the app's CLAUDE.md.
     */
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      // Text mode is left scrolling natively; its boxes sit above this canvas.
      const drawable = () => modeRef.current === "draw" && !disabledRef.current;

      // Re-anchored whenever the touch count changes, not just at the start:
      // the delta is measured against an average, so a finger joining or
      // leaving would otherwise move it under us and jump the paper.
      const anchorPan = (touches: TouchList, scrollParent: HTMLElement) => {
        panRef.current = {
          startAvgY: averageTouchY(touches),
          startScrollTop: scrollParent.scrollTop,
        };
      };

      const onTouchStart = (e: TouchEvent) => {
        if (!drawable()) return;
        if (e.cancelable) e.preventDefault();
        if (e.touches.length < 2) return;
        const scrollParent = scrollParentRef.current;
        if (!scrollParent) return;
        if (panRef.current) {
          anchorPan(e.touches, scrollParent);
          return;
        }
        // A sweep that has taken ink is real work, like a stroke past the
        // point threshold: the second finger is the hand, not a pan starting.
        const erase = eraseRef.current;
        if (erase && erase.swept.size > 0) return;
        const active = activeRef.current;
        if (active && active.points.length > PAN_TAKEOVER_MAX_POINTS) return;
        discardTouchStrokeRef.current();
        anchorPan(e.touches, scrollParent);
      };

      const onTouchMove = (e: TouchEvent) => {
        if (!drawable()) return;
        if (e.cancelable) e.preventDefault();
        const pan = panRef.current;
        const scrollParent = scrollParentRef.current;
        if (!pan || !scrollParent) return;
        scrollParent.scrollTop =
          pan.startScrollTop - (averageTouchY(e.touches) - pan.startAvgY);
      };

      // The finger left behind does not inherit the stroke; that needs a press.
      const onTouchEnd = (e: TouchEvent) => {
        if (!panRef.current) return;
        const scrollParent = scrollParentRef.current;
        if (e.touches.length >= 2 && scrollParent) {
          anchorPan(e.touches, scrollParent);
          return;
        }
        panRef.current = null;
      };

      // Without this an interrupted pan leaves the latch set, and the latch
      // refuses the next press — costing a stroke.
      const unsubscribeInterrupts = onPressInterrupt(() => {
        panRef.current = null;
      });

      canvas.addEventListener("touchstart", onTouchStart, { passive: false });
      canvas.addEventListener("touchmove", onTouchMove, { passive: false });
      canvas.addEventListener("touchend", onTouchEnd);
      canvas.addEventListener("touchcancel", onTouchEnd);
      return () => {
        unsubscribeInterrupts();
        canvas.removeEventListener("touchstart", onTouchStart);
        canvas.removeEventListener("touchmove", onTouchMove);
        canvas.removeEventListener("touchend", onTouchEnd);
        canvas.removeEventListener("touchcancel", onTouchEnd);
      };
    }, []);

    useDrawSurface(drawLockAdapter, !disabled && mode === "draw");

    return (
      <div
        ref={containerRef}
        className="relative w-full overflow-hidden rounded-[14px]"
        style={{ height: virtualHeight > 0 ? `${virtualHeight}px` : "100%" }}
      >
        <canvas
          ref={canvasRef}
          className="absolute inset-0 h-full w-full touch-none bg-transparent"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUpWithIdle}
          onPointerCancel={handlePointerCancelWithIdle}
        />
        <ScratchPadTextLayer
          items={textItems}
          mode={mode}
          activeId={activeTextId}
          containerRef={containerRef}
          onChangeText={onChangeText}
          onMoveText={onMoveText}
          onActivate={onActivateText}
          onRemove={onRemoveText}
        />
      </div>
    );
  },
);

ScratchPadCanvas.displayName = "ScratchPadCanvas";
